import { el, toast } from '../../dom';
import { ApiError } from '../../../core/network/api';
import { progressionApi, type OracleErrand } from '../../game-api';
import { playSfx } from '../../audio/audio';
import { richText } from '../../ui/icons';

/**
 * The birch-grove oracle's dialog: a small parchment-styled panel that shows the
 * robed oracle's daily errand ("bring me N of a resource for gold"), the player's
 * current progress, and a claim button that fulfils it. On success it pays coins
 * server-side; `onDone` lets the caller refresh the HUD wallet.
 *
 * Self-contained overlay: mounts to the given host, dims the world behind it, and
 * tears itself down on close.
 */
export function openOracleDialog(host: HTMLElement, onDone: () => void, onClose?: () => void): () => void {
  const layer = el('div', 'absolute inset-0 z-40 flex items-center justify-center bg-black/55 backdrop-blur-[1px]');
  const panel = el('div',
    'relative w-[min(92vw,420px)] rounded-2xl border-2 border-amber-300/60 bg-[#2a2018]/95 shadow-lift p-5 text-amber-50 font-body');
  layer.appendChild(panel);
  host.appendChild(layer);

  let closed = false;
  const close = () => { if (closed) return; closed = true; layer.remove(); onClose?.(); };
  layer.addEventListener('pointerdown', (e) => { if (e.target === layer) close(); });

  const render = (errand: OracleErrand | null, busy = false) => {
    panel.replaceChildren();
    panel.appendChild(el('button',
      'tb-close-x absolute top-2 right-3 text-amber-200/70 hover:text-amber-100 text-xl leading-none',
      { onClick: close }, '×'));
    panel.appendChild(el('div', 'text-center text-2xl font-heading tracking-wide text-amber-200 mb-1', {}, 'The Grove Oracle'));
    panel.appendChild(el('div', 'text-center text-xs uppercase tracking-[0.2em] text-amber-300/60 mb-3', {}, 'Birch Grove Shrine'));

    if (!errand) {
      panel.appendChild(el('div', 'text-center text-sm text-amber-100/80 py-6', {}, 'The oracle is silent…'));
      return;
    }

    panel.appendChild(el('p', 'text-sm italic text-amber-100/90 text-center leading-relaxed mb-4 px-2', {}, `“${errand.flavor}”`));

    const enough = errand.have >= errand.need;
    const bar = el('div', 'w-full h-2.5 rounded-full bg-black/40 overflow-hidden mb-1');
    const pct = Math.max(0, Math.min(1, errand.have / errand.need));
    bar.appendChild(el('div', `h-full ${enough ? 'bg-emerald-400' : 'bg-amber-400'}`, { style: `width:${Math.round(pct * 100)}%` }));
    panel.appendChild(bar);
    panel.appendChild(el('div', 'flex justify-between text-xs text-amber-100/80 mb-4', {},
      el('span', '', {}, `${errand.resourceLabel}: ${errand.have} / ${errand.need}`),
      // richText, not raw text: the coin/check glyphs must become pack sprites here
      // like everywhere else — as plain text they rendered as system emoji inside a
      // pixel-art panel (the two spots the icon sweep missed).
      el('span', 'text-amber-300 font-semibold inline-flex items-center gap-1', {}, ...richText(`Reward: ${errand.reward} 🪙`))));

    if (errand.claimedToday) {
      panel.appendChild(el('div', 'text-center text-sm text-emerald-300/90 py-2 inline-flex items-center justify-center gap-1 w-full', {},
        ...richText('✓ Blessed today — return tomorrow for a new errand.')));
      return;
    }

    const btn = el('button',
      `w-full py-2.5 rounded-lg font-heading tracking-wide transition ${errand.ready && !busy
        ? 'bg-amber-500 hover:bg-amber-400 text-[#2a2018] cursor-pointer'
        : 'bg-amber-900/40 text-amber-200/40 cursor-not-allowed'}`,
      {}, busy ? 'Offering…' : errand.ready ? `Give ${errand.need} ${errand.resourceLabel}` : `Need ${Math.max(0, errand.need - errand.have)} more`);
    if (errand.ready && !busy) {
      (btn as HTMLButtonElement).addEventListener('click', () => { void claim(); });
    }
    panel.appendChild(btn);
  };

  const claim = async () => {
    render(current, true);
    try {
      const r = await progressionApi.oracleClaim();
      playSfx('quest_complete');
      toast(`The oracle rewards you: +${r.reward} 🪙`);
      onDone();
      close();
    } catch (e) {
      // Two bugs in one line, both of which made a REFUSAL look like a success:
      // the message was read in axios' `e.response.data.message` shape, but the
      // network layer throws an ApiError carrying a plain `.message` (so the real
      // reason was always thrown away for the fallback) — and the toast used the
      // DEFAULT 'ok' kind, painting "you don't have enough" in success green.
      toast(e instanceof ApiError ? e.message : 'The oracle turns away…', 'error');
      void load();
    }
  };

  let current: OracleErrand | null = null;
  const load = async () => {
    try {
      current = await progressionApi.oracle();
    } catch {
      current = null;
    }
    render(current);
  };

  render(null);
  void load();
  return close;
}
